import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type ApiClient, ApiRequestError, sessionFromLogin } from '@proctoring/api-client';
import type {
  LoginRequestInput,
  PasswordSetupRequest,
  ResetPasswordRequest,
  User,
} from '@proctoring/shared';

export type AuthStatus =
  /** Re-validating a token restored from localStorage; render nothing route-dependent yet. */
  'bootstrapping' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  api: ApiClient;
  login: (credentials: LoginRequestInput) => Promise<User>;
  setupPassword: (request: PasswordSetupRequest) => Promise<User>;
  resetPassword: (request: ResetPasswordRequest) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  api: ApiClient;
  children: ReactNode;
}

/**
 * Owns the session: the in-memory JWT, the `/auth/me` bootstrap, and sign-out.
 *
 * A token restored from `localStorage` is never trusted on its own — the provider spends
 * one `/auth/me` round-trip before declaring the user authenticated. That is what makes a
 * revoked invitation, a locked account, or a lapsed candidate 72h window take effect on
 * the next page load instead of at the token's expiry (the API re-loads the user per
 * request for the same reason; see `requireAuth`).
 */
export function AuthProvider({ api, children }: AuthProviderProps): ReactElement {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() =>
    api.tokens.get() ? 'bootstrapping' : 'anonymous',
  );

  const signOut = useCallback(() => {
    api.tokens.clear();
    setUser(null);
    setStatus('anonymous');
    // Any cached page is scoped to the user who fetched it; keeping it would leak one
    // account's interviews into the next sign-in on a shared machine.
    queryClient.clear();
  }, [api, queryClient]);

  // The client raises this from any 401, including one fired mid-render by a background
  // refetch, so a token that expires while the tab sits open lands on /login promptly.
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  useEffect(
    () =>
      api.tokens.subscribe((token) => {
        if (token !== null) return;
        // Status has to move too, not just the user. The guards accept `!user` so the
        // redirect already worked, but leaving status on `authenticated` with no user is a
        // contradiction the rest of the tree can read, and the cached pages belong to the
        // account that just lost its token.
        setUser(null);
        setStatus('anonymous');
        queryClient.clear();
      }),
    [api, queryClient],
  );

  useEffect(() => {
    if (!api.tokens.get()) return;

    let cancelled = false;
    const controller = new AbortController();

    void (async () => {
      try {
        const me = await api.me(controller.signal);
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
      } catch (error) {
        if (cancelled) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // A network blip must not silently sign the user out and lose their place; only a
        // definitive rejection from the API clears the stored token.
        if (error instanceof ApiRequestError) {
          signOutRef.current();
          return;
        }
        setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [api]);

  const login = useCallback(
    async (credentials: LoginRequestInput) => {
      const response = await api.login(credentials);
      api.tokens.set(sessionFromLogin(response));
      setUser(response.user);
      setStatus('authenticated');
      return response.user;
    },
    [api],
  );

  const setupPassword = useCallback(
    async (request: PasswordSetupRequest) => {
      const response = await api.setupPassword(request);
      api.tokens.set(sessionFromLogin(response));
      setUser(response.user);
      setStatus('authenticated');
      return response.user;
    },
    [api],
  );

  // The API answers a completed reset with a full login response, so the new password is
  // live immediately and the user never retypes it at /login.
  const resetPassword = useCallback(
    async (request: ResetPasswordRequest) => {
      const response = await api.resetPassword(request);
      api.tokens.set(sessionFromLogin(response));
      setUser(response.user);
      setStatus('authenticated');
      return response.user;
    },
    [api],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, api, login, setupPassword, resetPassword, logout: signOut }),
    [status, user, api, login, setupPassword, resetPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

/** The API client on its own, for hooks that issue queries without needing the session. */
export function useApi(): ApiClient {
  return useAuth().api;
}
